import React, { useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { diff } from 'deep-object-diff';
import { saveAs } from 'file-saver';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import FunnelIcon from 'icons/funnel.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useCreateProjectsAnalyticsFilterSelectionConfigurationsBulkUpdateProcessMutation } from 'components/hooks/projectsAnalyticsFilterSelectionConfigurationsBulkUpdateProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsApprovalStatusReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsApprovalStatusReportsExportProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsCurrentProjectsReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsCurrentProjectsReportsExportProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsProjectsCreatedReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsProjectsCreatedReportsExportProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsReviewStatusReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsReviewStatusReportsExportProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsSignatureStatusReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsSignatureStatusReportsExportProcessMutations';
import { useCreateProjectsAnalyticsProjectsReportsTopRisksReportsExportProcessMutation } from 'components/hooks/projectsAnalyticsProjectsReportsTopRisksReportsExportProcessMutations';
import { useProjectsReportsExportProcessQuery } from 'components/hooks/projectsReportsExportProcessQueries';
import useDebounce from 'components/hooks/useDebounce';
import useDocumentVisibilityChange from 'components/hooks/useDocumentVisibilityChange';

import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';

import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import ApprovalStatusCard from 'components/projectsAnalytics/components/ApprovalStatusCard';
import CurrentProjectsCard from 'components/projectsAnalytics/components/CurrentProjectsCard';
import Grid from 'components/projectsAnalytics/components/Grid';
import ProjectsAnalyticsDivisionsFilterBanner from 'components/projectsAnalytics/components/ProjectsAnalyticsDivisionsFilterBanner';
import ProjectsAnalyticsFiltersSidePanel from 'components/projectsAnalytics/components/ProjectsAnalyticsFiltersSidePanel';
import ProjectsCreatedCard from 'components/projectsAnalytics/components/ProjectsCreatedCard';
import ReviewStatusCard from 'components/projectsAnalytics/components/ReviewStatusCard';
import SignatureStatusCard from 'components/projectsAnalytics/components/SignatureStatusCard';
import TopRisksCard from 'components/projectsAnalytics/components/TopRisksCard';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function ProjectsAnalyticsDashboardView({
  domainFilterSelectionConfiguration,
  isFilterable,
}) {
  const { addInedibleToast } = useBreadBoard();

  const {
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isProjectReviewDatesEnabled,
  } = useProjectsAnalyticsDashboardContext();

  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    divisionIds: new Set(
      Object.keys(domainFilterSelectionConfiguration.included.company),
    ),
  });

  const [debouncedFilters] = useDebounce(filters, 250);
  const [isFiltersSidePanelOpen, setIsFiltersSidePanelOpen] = useState(false);

  const { t: i18nTShared } = useTranslation('common', { keyPrefix: 'shared' });

  const createApprovalStatusReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsApprovalStatusReportsExportProcessMutation();

  const createCurrentProjectsReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsCurrentProjectsReportsExportProcessMutation();

  const createProjectsCreatedReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsProjectsCreatedReportsExportProcessMutation();

  const createReviewStatusReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsReviewStatusReportsExportProcessMutation();

  const createSignatureStatusReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsSignatureStatusReportsExportProcessMutation();

  const createTopRisksReportsExportProcessMutation =
    useCreateProjectsAnalyticsProjectsReportsTopRisksReportsExportProcessMutation();

  const exportProcessId =
    createApprovalStatusReportsExportProcessMutation.data?.data.id ||
    createCurrentProjectsReportsExportProcessMutation.data?.data.id ||
    createProjectsCreatedReportsExportProcessMutation.data?.data.id ||
    createReviewStatusReportsExportProcessMutation.data?.data.id ||
    createSignatureStatusReportsExportProcessMutation.data?.data.id ||
    createTopRisksReportsExportProcessMutation.data?.data.id;

  const {
    data: domainExportProcess,
    isError: isDomainExportProcessFetchError,
  } = useProjectsReportsExportProcessQuery({
    exportProcessId,
    queryOptions: { enabled: !!exportProcessId },
  });

  const createFilterSelectionConfigurationsBulkUpdateProcessMutation =
    useCreateProjectsAnalyticsFilterSelectionConfigurationsBulkUpdateProcessMutation();

  const dashboardFilterParams = {
    ...(debouncedFilters.divisionIds.size > 0 && {
      division_ids: [...debouncedFilters.divisionIds],
    }),
  };

  const initialDocumentVisibilityChangeFilters = useRef(dashboardFilterParams);

  const documentVisibilityChangeFilters = useRef();
  documentVisibilityChangeFilters.current = dashboardFilterParams;

  useDocumentVisibilityChange((visibilityState) => {
    if (visibilityState === 'visible') {
      initialDocumentVisibilityChangeFilters.current =
        documentVisibilityChangeFilters.current;
    }

    const documentVisibilityChangeFiltersDiff = diff(
      initialDocumentVisibilityChangeFilters.current,
      documentVisibilityChangeFilters.current,
    );

    if (
      visibilityState === 'hidden' &&
      Object.keys(documentVisibilityChangeFiltersDiff).length > 0
    ) {
      createFilterSelectionConfigurationsBulkUpdateProcessMutation.mutate({
        bulkUpdateProcessParams: {
          company_ids:
            documentVisibilityChangeFilters.current.division_ids || [],
        },
      });
    }
  });

  const handleApprovalStatusExportClick = ({ params }) => {
    createApprovalStatusReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  const handleCurrentProjectsExportClick = ({ params }) => {
    createCurrentProjectsReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  const handleExportReset = () => {
    createApprovalStatusReportsExportProcessMutation.reset();
    createCurrentProjectsReportsExportProcessMutation.reset();
    createProjectsCreatedReportsExportProcessMutation.reset();
    createReviewStatusReportsExportProcessMutation.reset();
    createSignatureStatusReportsExportProcessMutation.reset();
    createTopRisksReportsExportProcessMutation.reset();

    if (exportProcessId) {
      queryClient.removeQueries({
        queryKey: ['projectsReports', 'exportProcesses', exportProcessId],
        exact: true,
      });
    }
  };

  const handleFilterChange = ({ filter, value }) => {
    setFilters((prevFilters) => ({ ...prevFilters, [filter]: new Set(value) }));
  };

  const handleProjectsCreatedExportClick = ({ params }) => {
    createProjectsCreatedReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  const handleReviewStatusExportClick = ({ params }) => {
    createReviewStatusReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  const handleSignatureStatusExportClick = ({ params }) => {
    createSignatureStatusReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  const handleTopRisksExportClick = ({ params }) => {
    createTopRisksReportsExportProcessMutation.mutate(
      { params },
      { onError: addInedibleToast },
    );
  };

  if (domainExportProcess?.data.attributes.status === 'completed') {
    saveAs(domainExportProcess.data.attributes.url);
    handleExportReset();
  }

  if (isDomainExportProcessFetchError) handleExportReset();

  if (domainExportProcess?.data.attributes.status === 'failed') {
    addInedibleToast();
    handleExportReset();
  }

  const activeFiltersCount = Object.keys(dashboardFilterParams).length;

  const isGeneratingExport =
    !(
      createApprovalStatusReportsExportProcessMutation.isIdle ||
      createCurrentProjectsReportsExportProcessMutation.isIdle ||
      createProjectsCreatedReportsExportProcessMutation.isIdle ||
      createReviewStatusReportsExportProcessMutation.isIdle ||
      createSignatureStatusReportsExportProcessMutation.isIdle ||
      createTopRisksReportsExportProcessMutation.isIdle
    ) || !!exportProcessId;

  return (
    <>
      {isFilterable && (
        <>
          <div className='tw-flex tw-justify-center tw-pb-6'>
            <div className='tw-flex tw-w-full tw-max-w-[1164px] tw-items-center tw-gap-3'>
              <div className='tw-flex-1'>
                <ProjectsAnalyticsDivisionsFilterBanner
                  divisionIdsFilter={filters.divisionIds}
                />
              </div>
              <div className='tw-flex tw-justify-end'>
                <Options
                  btnToggle={(isOpen) => (
                    <button
                      className={classNames(
                        'tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-1.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100',
                        isOpen && 'tw-border-blue-100 tw-bg-blue-050',
                      )}
                      type='button'
                    >
                      <FunnelIcon height={12} width={12} />
                      <span>{i18nTShared('filter')}</span>
                      {activeFiltersCount > 0 && (
                        <span className='tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050 tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-900 group-hover/button:tw-bg-blue-100 group-active/button:tw-bg-blue-200'>
                          {activeFiltersCount}
                        </span>
                      )}
                      <ChevronUpIcon
                        className={classNames(!isOpen && 'tw-hidden')}
                        height={20}
                        width={20}
                      />
                      <ChevronDownIcon
                        className={classNames(isOpen && 'tw-hidden')}
                        height={20}
                        width={20}
                      />
                    </button>
                  )}
                  listStyle='tw-w-52 [&_li]:tw-border-none [&_li]:tw-px-1 [&_li]:tw-py-0.5 tw-z-[1000]'
                  toggleStyle='tw-inline-flex [&_ul]:tw-border-blue-100 [&_ul]:tw-rounded-lg'
                >
                  <OptionChoice
                    onClick={(setOptionsOpen) => {
                      setIsFiltersSidePanelOpen(true);
                      setOptionsOpen(false);
                    }}
                  >
                    <button
                      className='tw-flex tw-h-9 tw-w-full tw-items-center tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700'
                      type='button'
                    >
                      {i18nTShared('divisionsParentheses')}
                    </button>
                  </OptionChoice>
                </Options>
              </div>
            </div>
          </div>
          <ProjectsAnalyticsFiltersSidePanel
            filters={filters}
            isOpen={isFiltersSidePanelOpen}
            onClose={() => setIsFiltersSidePanelOpen(false)}
            onFilterChange={handleFilterChange}
          />
        </>
      )}
      <Grid>
        <CurrentProjectsCard
          dashboardFilterParams={dashboardFilterParams}
          isGeneratingExport={isGeneratingExport}
          onExportClick={handleCurrentProjectsExportClick}
        />
        <TopRisksCard
          dashboardFilterParams={dashboardFilterParams}
          isGeneratingExport={isGeneratingExport}
          onExportClick={handleTopRisksExportClick}
        />
        {isApprovalsEnabled && (
          <ApprovalStatusCard
            dashboardFilterParams={dashboardFilterParams}
            isGeneratingExport={isGeneratingExport}
            onExportClick={handleApprovalStatusExportClick}
          />
        )}
        {isDigitalSignaturesEnabled && (
          <SignatureStatusCard
            dashboardFilterParams={dashboardFilterParams}
            isGeneratingExport={isGeneratingExport}
            onExportClick={handleSignatureStatusExportClick}
          />
        )}
        {isProjectReviewDatesEnabled && (
          <ReviewStatusCard
            dashboardFilterParams={dashboardFilterParams}
            isGeneratingExport={isGeneratingExport}
            onExportClick={handleReviewStatusExportClick}
          />
        )}
        <ProjectsCreatedCard
          dashboardFilterParams={dashboardFilterParams}
          isGeneratingExport={isGeneratingExport}
          onExportClick={handleProjectsCreatedExportClick}
        />
      </Grid>
    </>
  );
}

ProjectsAnalyticsDashboardView.propTypes = {
  domainFilterSelectionConfiguration: PropTypes.shape({
    data: resourceShape('filterSelectionConfiguration'),
    included: PropTypes.shape({
      company: PropTypes.objectOf(resourceShape('company')).isRequired,
    }).isRequired,
  }).isRequired,
  isFilterable: PropTypes.bool.isRequired,
};
